import { DonutChartWidget } from "../charts/DonutChartWidget";
import { LineChartWidget } from "../charts/LineChartWidget";
import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtPercent } from "./formatters";

interface ActionsData {
  bySource: { meeting: number; task: number; manual: number };
  byStatus: { open: number; inProgress: number; completed: number; cancelled: number };
  completionRate: number;
  avgDaysToComplete: number;
  overdueByAssignee: Array<{ name: string; overdueCount: number }>;
  weeklyTrend: Array<{ week: string; created: number; completed: number }>;
  summary: { total: number; completedOnTime: number; completedLate: number; overdue: number };
}

interface AssigneeRow {
  name: string;
  overdueCount: number;
}

export function ActionsTrackerReport({ data }: { data: ActionsData }) {
  const sourceDonut = [
    { name: "Meeting", value: data.bySource.meeting, color: "hsl(210,100%,50%)" },
    { name: "Task", value: data.bySource.task, color: "hsl(38,92%,50%)" },
    { name: "Manual", value: data.bySource.manual, color: "hsl(270,75%,55%)" },
  ];

  const cols: Array<TableColumn<AssigneeRow>> = [
    { key: "name", label: "Assignee" },
    {
      key: "overdueCount",
      label: "Overdue Count",
      align: "right",
      render: (r) => (
        <span className="text-[hsl(0,72%,45%)] font-medium">{r.overdueCount}</span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Total Actions" value={data.summary.total} />
        <StatCard
          label="Completion Rate"
          value={fmtPercent(data.completionRate)}
          tone={data.completionRate > 70 ? "good" : "warn"}
        />
        <StatCard
          label="On Time"
          value={data.summary.completedOnTime}
          tone="good"
        />
        <StatCard
          label="Late"
          value={data.summary.completedLate}
          tone="warn"
        />
        <StatCard label="Overdue" value={data.summary.overdue} tone="bad" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ReportSection title="By Source">
          <DonutChartWidget data={sourceDonut} />
        </ReportSection>
        <ReportSection
          title="Weekly Trend"
          subtitle={`Avg ${data.avgDaysToComplete} days to complete`}
        >
          <LineChartWidget
            data={data.weeklyTrend}
            xKey="week"
            lines={[
              { key: "created", name: "Created", color: "hsl(210,100%,50%)" },
              { key: "completed", name: "Completed", color: "hsl(142,72%,40%)" },
            ]}
          />
        </ReportSection>
      </div>

      <ReportSection title="Overdue by Assignee">
        <ReportTable
          columns={cols}
          rows={data.overdueByAssignee}
          rowKey={(r) => r.name}
          empty="No overdue actions."
        />
      </ReportSection>
    </div>
  );
}
